import { faFlag, faMemoCircleCheck } from "@fortawesome/pro-solid-svg-icons"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { Box, Button, Grid, Typography } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { AppContext } from "components/AppContextProvider"
import { CircleIcon } from "components/icons/CircleIcon"
import theme from "css/theme/light"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import {
  issueReportStatusAlertBackgroundColor,
  issueReportStatusAlertColor,
  issueReportStatusColor
} from "helpers/IssueReports/IssueReportStatusColor"
import { issueReportStatusLabel } from "helpers/IssueReports/IssueReportStatusLabel"
import {
  workorderStatusAlertBackgroundColor,
  workorderStatusAlertColor,
  workorderStatusColor
} from "helpers/WorkOrder/WorkOrderStatusColor"
import { workorderStatusLabel } from "helpers/WorkOrder/WorkOrderStatusLabel"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Moment from "react-moment"
import { Link } from "react-router-dom"

const assignmentTranslation = (assignment, t) => {
  switch (assignment.assignmentType) {
    case 1:
      return {
        title: t("issuereports.issuereport", "Issue report"),
        route: allRoutes.IssuereportsArchiveIssuereport(assignment.id),
        routeTitle: t("issuereports.edit_issuereport", "Edit issue report"),
        icon: faFlag
      }
    case 2:
      return {
        title: t("global.workorder", "Work order"),
        route: allRoutes.IssuereportsWorkordersDetails(assignment.id),
        routeTitle: t("issuereports.open_workorder", "Open work order"),
        icon: faFlag
      }
    default:
      return null
  }
}

const DashboardCard = ({ assignment, filteredIssueReports }) => {
  const [assignmentData, setAssignmentData] = useState()
  const { t } = useTranslation()
  const appContext = useContext(AppContext)

  useEffect(() => {
    setAssignmentData(assignmentTranslation(assignment, t))
  }, [assignment])

  return (
    <Box
      gap={2}
      mb={filteredIssueReports && filteredIssueReports.length === 1 ? 0 : 2}
      p={2.5}
      sx={{
        borderBottom: filteredIssueReports && filteredIssueReports.length === 1 ? 0 : "1px solid",
        borderColor:
          filteredIssueReports && filteredIssueReports.length === 1 ? "transparent" : theme.palette.grey[200]
      }}>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Box mr={3}>
          <CircleIcon
            icon={assignment.assignmentType === 1 ? faFlag : faMemoCircleCheck}
            color={`${
              assignment.assignmentType === 1
                ? issueReportStatusAlertColor(assignment.status)
                : workorderStatusAlertColor(assignment.status)
            }`}
            backgroundColor={`${
              assignment.assignmentType === 1
                ? issueReportStatusAlertBackgroundColor(assignment.status)
                : workorderStatusAlertBackgroundColor(assignment.status)
            }`}
            size="lg"
          />
        </Box>
        <Grid container>
          <Grid sm={12} display="flex" flexDirection="row" flexWrap="wrap" alignItems="center">
            <Box pr={1}>
              {assignment?.assignmentType === 1 ? (
                <Typography variant="bodySmall" color={`${issueReportStatusColor(assignment.status)}.700`}>
                  {issueReportStatusLabel(assignment.status, t)}
                </Typography>
              ) : (
                <Typography variant="bodySmall" color={`${workorderStatusColor(assignment.status)}.700`}>
                  {workorderStatusLabel(assignment.status, t)}
                </Typography>
              )}
            </Box>
            <Box pr={1}>
              <Typography variant="bodySmall">{assignmentData?.title}</Typography>
            </Box>
            <Box pr={1}>
              <Typography variant="bodySmall" fontWeight="600">
                {assignment?.label ? `${assignment.assignmentType === 2 ? "#" : ""}${assignment.label}` : "-"}
              </Typography>
            </Box>
            <Box flexGrow={2}>
              <Typography variant="bodySmall" color="textSecondary">
                {assignment.secondaryLabel ? assignment.secondaryLabel : "-"}
              </Typography>
            </Box>

            <Box>
              <Typography variant="bodySmall" color="textSecondary ">
                {assignment?.updatedDate ? (
                  <Moment utc local format="YYYY-MM-DD HH:mm">
                    {assignment.updatedDate}
                  </Moment>
                ) : (
                  "-"
                )}
              </Typography>
            </Box>
          </Grid>

          <Grid md={9} xl={10} xs={12} pt={1}>
            <Typography variant="bodyMedium" fontWeight={600} sx={{ wordBreak: "break-word" }}>
              {assignment?.description ? assignment.description : "-"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {hasRole(appContext.appUser, allRoles.IssueReportsAdmin) && (
        <Grid container direction="row" justifyContent="flex-end" alignItems="center">
          <Button
            component={Link}
            variant="text"
            to={assignmentData?.route}
            endIcon={<OpenInNewIcon style={{ color: theme.palette.secondary.main }} />}
            fontSize="inherit">
            <Typography variant="labelSmallBold" color="textPrimary">
              {assignmentData?.routeTitle}
            </Typography>
          </Button>
        </Grid>
      )}
    </Box>
  )
}

export default DashboardCard
